import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import {
    Button,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';

import { dynamicFieldsMigrationStatusQueryOptions, useCompleteDynamicFieldsMigrationMutation, useMigrateDynamicFieldsMutation } from '@/api/dynamicFieldsQueryOptions';
import { contactgroupsMigrationStatusQueryOptions, eventsMigrationStatusQueryOptions, listsMigrationStatusQueryOptions, useMigrateContactgroupsMutation, useMigrateEventsMutation, useMigrateListsMutation } from '@/api/listQueryOptions';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import LoadProgress from '@/components/LoadProgress';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/migrations',
)({
    component: MigrationsComponent,
})

function MigrationsComponent() {
    const params = Route.useParams()

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', params.organizationUuid, ['migrateContactgroups', 'migrateLists', 'migrateEvents', 'migrateDynamicFields']))
    const permissions = permissionsQuery.data

    const onSuccess = () => {
        enqueueSnackbar(t('action.success'), {
            variant: 'success',
        })
    }

    const onError = (error: Error) => {
        enqueueSnackbar(error.message, {
            variant: 'error',
        })
    }

    const contactgroupsMigrationStatusQuery = useSuspenseQuery(contactgroupsMigrationStatusQueryOptions())
    const contactgroupsMigrationStatus = contactgroupsMigrationStatusQuery.data
    const eventsMigrationStatusQuery = useSuspenseQuery(eventsMigrationStatusQueryOptions())
    const eventsMigrationStatus = eventsMigrationStatusQuery.data
    const listsMigrationStatusQuery = useSuspenseQuery(listsMigrationStatusQueryOptions())
    const listsMigrationStatus = listsMigrationStatusQuery.data

    const migrateContactgroups = useMigrateContactgroupsMutation(onSuccess, onError)
    const migrateLists = useMigrateListsMutation(onSuccess, onError)
    const migrateEvents = useMigrateEventsMutation(onSuccess, onError)

    const dynamicFieldsMigrationStatusQuery = useSuspenseQuery(dynamicFieldsMigrationStatusQueryOptions())
    const dynamicFieldsMigrationStatus = dynamicFieldsMigrationStatusQuery.data
    const migrateDynamicFields = useMigrateDynamicFieldsMutation(onSuccess, onError)
    const completeDynamicFields = useCompleteDynamicFieldsMigrationMutation(onSuccess, onError)

    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }

    if (contactgroupsMigrationStatusQuery.isLoading || contactgroupsMigrationStatusQuery.isFetching) {
        return <LoadProgress />
    }
    if (listsMigrationStatusQuery.isLoading || listsMigrationStatusQuery.isFetching) {
        return <LoadProgress />
    }
    if (eventsMigrationStatusQuery.isLoading || eventsMigrationStatusQuery.isFetching) {
        return <LoadProgress />
    }
    if (dynamicFieldsMigrationStatusQuery.isLoading || dynamicFieldsMigrationStatusQuery.isFetching) {
        return <LoadProgress />
    }

    const migrateContactgroupCondition = permissions && permissions.filter(p => p.detail === 'migrateContactgroups' && p.permissions.some(a => a === 'write')).length > 0
    const migrateListsCondition = permissions && permissions.filter(p => p.detail === 'migrateLists' && p.permissions.some(a => a === 'write')).length > 0
    const migrateEventsCondition = permissions && permissions.filter(p => p.detail === 'migrateEvents' && p.permissions.some(a => a === 'write')).length > 0
    const migrateDynamicFieldsCondition = permissions && permissions.filter(p => p.detail === 'migrateDynamicFields' && p.permissions.some(a => a === 'write')).length > 0

    return (
        <>
            <Stack spacing={2} direction="column" sx={{
                maxWidth: '300px'
            }}>

                {(migrateContactgroupCondition && contactgroupsMigrationStatus.totalItems > 0) &&
                    <>
                        <Typography>
                            {t('label.migrations.contactgroups', { migrated: contactgroupsMigrationStatus.migratedItems, total: contactgroupsMigrationStatus.totalItems })}
                        </Typography>
                        <Button variant="contained" onClick={() => {
                            migrateContactgroups.mutate()
                        }}>
                            {t('button.migrations.contactgroups')}
                        </Button>
                    </>
                }

                {(migrateListsCondition && listsMigrationStatus.totalItems > 0) &&
                    <>
                        <Typography>
                            {t('label.migrations.lists', { migrated: listsMigrationStatus.migratedItems, total: listsMigrationStatus.totalItems })}
                        </Typography>
                        <Button variant="contained" onClick={() => {
                            migrateLists.mutate()
                        }}>
                            {t('button.migrations.lists')}
                        </Button>
                    </>
                }


                {(migrateEventsCondition && eventsMigrationStatus.totalItems > 0) &&
                    <>
                        <Typography>
                            {t('label.migrations.events', { migrated: eventsMigrationStatus.migratedItems, total: eventsMigrationStatus.totalItems })}
                        </Typography>
                        <Button variant="contained" onClick={() => {
                            migrateEvents.mutate()
                        }}>
                            {t('button.migrations.events')}
                        </Button>
                    </>
                }

                {(migrateDynamicFieldsCondition && dynamicFieldsMigrationStatus.totalItems > 0) &&
                    <>
                        <Typography>
                            {t('label.migrations.dynamicFields', { migrated: dynamicFieldsMigrationStatus.migratedItems, total: dynamicFieldsMigrationStatus.totalItems })}
                        </Typography>
                        <Tooltip title={t('tooltip.migrations.dynamicFields')}>

                            <Button variant="contained" onClick={() => {
                                migrateDynamicFields.mutate()
                            }}>
                                {t('button.migrations.dynamicFields')}
                            </Button>
                        </Tooltip>
                    </>
                }
                {(migrateDynamicFieldsCondition && dynamicFieldsMigrationStatus.totalItems > 0 && dynamicFieldsMigrationStatus.migratedItems > 0) &&
                    <>
                        <Tooltip title={t('tooltip.migrations.dynamicFieldsComplete')}>
                            <Button variant="contained" color='secondary' onClick={() => {
                                completeDynamicFields.mutate()
                            }}>
                                {t('button.migrations.dynamicFieldsComplete')}
                            </Button>
                        </Tooltip>
                    </>

                }

            </Stack>
        </>
    );
}
